import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryName } from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-error-warning-line text-3xl text-secondary-500"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2 font-heading">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line mr-2"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '澳门彩官方指南',
    },
  };

  const categoryName = getCategoryName(article.category);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <article className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName, to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                  {categoryName}
                </span>
                <span className="text-xs text-foreground-500">{article.publishDate}</span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
                {article.isHot && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                    热门
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 leading-tight font-heading">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 澳门彩官方指南`}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((section, index) => {
                if (section.type === 'heading') {
                  const headingLevel = index === 0 ? 2 : 3;
                  const Tag = `h${headingLevel}` as keyof JSX.IntrinsicElements;
                  return (
                    <Tag
                      key={index}
                      className={`font-bold text-foreground-950 mt-8 mb-3 font-heading ${
                        headingLevel === 2 ? 'text-xl' : 'text-lg'
                      }`}
                    >
                      {section.content}
                    </Tag>
                  );
                }
                if (section.type === 'paragraph') {
                  return (
                    <p
                      key={index}
                      className="text-sm text-foreground-700 leading-relaxed mb-4"
                    >
                      {section.content}
                    </p>
                  );
                }
                if (section.type === 'list' && section.items) {
                  return (
                    <ul key={index} className="mb-4 space-y-2">
                      {section.items.map((item, i) => (
                        <li
                          key={i}
                          className="text-sm text-foreground-700 leading-relaxed flex gap-2"
                        >
                          <span className="text-accent-500 mt-1 flex-shrink-0">
                            <i className="ri-checkbox-circle-line"></i>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (section.type === 'tip') {
                  return (
                    <div
                      key={index}
                      className="my-6 p-4 rounded-lg bg-primary-50 border border-primary-200/60 flex gap-3"
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-lightbulb-line text-primary-600"></i>
                      </div>
                      <p className="text-sm text-foreground-800 leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14">
                <h3 className="text-lg font-bold text-foreground-950 mb-5 font-heading">
                  相关文章推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group block p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
                    >
                      <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-accent-600 transition-colors duration-200 font-heading">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                        {ra.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}